import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Grid, IconButton, Typography } from "@mui/material";
import SavingsIcon from "@mui/icons-material/Savings";
import PublicIcon from "@mui/icons-material/Public";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HandshakeIcon from "@mui/icons-material/Handshake";
import TuneIcon from "@mui/icons-material/Tune";
import VerifiedIcon from "@mui/icons-material/Verified";
import PaidIcon from "@mui/icons-material/Paid";
import GppGoodIcon from "@mui/icons-material/GppGood";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SchoolIcon from "@mui/icons-material/School";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import CampaignIcon from "@mui/icons-material/Campaign";
import DescriptionIcon from "@mui/icons-material/Description";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MapIcon from "@mui/icons-material/Map";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

const PARTNER_APPLICATION_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfN4r1burfyftWepKIxGM7KkDQOAVK_fhccqnsusk5gCEzrPQ/viewform?usp=dialog";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type InViewResultT = {
  ref: React.RefObject<HTMLDivElement | null>;
  inView: boolean;
};

const useInView = (threshold = 0.15): InViewResultT => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
};

type FeatureItemT = {
  icon: React.ReactNode;
  title: string;
  body: string;
};

const FEATURE_GLOWS = [
  {
    top: 0,
    left: 0,
    right: "auto",
    bottom: "auto",
    background: "radial-gradient(ellipse at top left, rgba(56,189,248,0.18) 0%, transparent 70%)",
  },
  {
    top: 0,
    left: "auto",
    right: 0,
    bottom: "auto",
    background: "radial-gradient(ellipse at top right, rgba(103,232,249,0.15) 0%, transparent 70%)",
  },
  {
    top: "auto",
    left: "auto",
    right: 0,
    bottom: 0,
    background: "radial-gradient(ellipse at bottom right, rgba(59,130,246,0.17) 0%, transparent 70%)",
  },
  {
    top: "auto",
    left: 0,
    right: "auto",
    bottom: 0,
    background: "radial-gradient(ellipse at bottom left, rgba(56,189,248,0.16) 0%, transparent 70%)",
  },
];

type FeatureRowPropsT = {
  feature: FeatureItemT;
  index: number;
};

const FeatureRow = ({ feature, index }: FeatureRowPropsT) => {
  const glow = FEATURE_GLOWS[index % FEATURE_GLOWS.length];
  return (
    <Box sx={{ ...styles.featureRowCard, position: "relative", overflow: "hidden" }}>
      <Box
        sx={{
          position: "absolute",
          top: glow.top,
          left: glow.left,
          right: glow.right,
          bottom: glow.bottom,
          width: "70%",
          height: "70%",
          background: glow.background,
          pointerEvents: "none",
        }}
      />
      <Box sx={{ ...styles.iconBox, position: "relative" }}>{feature.icon}</Box>
      <Box>
        <Typography sx={styles.featureRowTitle}>{feature.title}</Typography>
        <Typography sx={styles.featureRowBody}>{feature.body}</Typography>
      </Box>
    </Box>
  );
};

type HeroSectionPropsT = {
  t: TranslateFnT;
};

const HeroSection = ({ t }: HeroSectionPropsT) => {
  const { ref, inView } = useInView();

  return (
    <Box sx={styles.heroSection}>
      <Box component="img" src="/img/assets/Install-2.jpg" alt="" sx={styles.heroBackgroundImage} />
      <Box sx={styles.heroRadialOverlay} />
      <Box sx={styles.heroGlowOverlay} />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "180px",
          background: "linear-gradient(to bottom, rgba(4,9,34,0.65) 0%, transparent 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, py: { xs: 10, md: 14 }, width: "100%" }}>
        <Box
          ref={ref}
          sx={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrow}>{t("hero.eyebrow")}</Typography>
          <Typography component="h1" sx={styles.heroHeading}>
            {t("hero.headlineStart")}{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("hero.headlineGradient")}
            </Box>{" "}
            {t("hero.headlineEnd")}
          </Typography>
          <Typography sx={styles.heroSubheading}>{t("hero.subheading")}</Typography>
          <Button
            href={PARTNER_APPLICATION_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            sx={styles.ctaButton}
          >
            {t("hero.cta")}
          </Button>
        </Box>
      </Container>
      <Box sx={styles.heroBottomGradient} />
    </Box>
  );
};

type BuildOrSellSectionPropsT = {
  t: TranslateFnT;
};

const BuildOrSellSection = ({ t }: BuildOrSellSectionPropsT) => {
  const { ref: refCard1, inView: inView1 } = useInView();
  const { ref: refCard2, inView: inView2 } = useInView();
  const { ref: refBottom, inView: inViewBottom } = useInView();

  const buildFeatures: FeatureItemT[] = [
    {
      icon: <SavingsIcon sx={{ fontSize: 20, color: "#38bdf8" }} />,
      title: t("buildOrSell.buildFeatures.costSavings.title"),
      body: t("buildOrSell.buildFeatures.costSavings.body"),
    },
    {
      icon: <PublicIcon sx={{ fontSize: 20, color: "#38bdf8" }} />,
      title: t("buildOrSell.buildFeatures.expandReach.title"),
      body: t("buildOrSell.buildFeatures.expandReach.body"),
    },
    {
      icon: <AdminPanelSettingsIcon sx={{ fontSize: 20, color: "#38bdf8" }} />,
      title: t("buildOrSell.buildFeatures.seamlessAdmin.title"),
      body: t("buildOrSell.buildFeatures.seamlessAdmin.body"),
    },
    {
      icon: <ElectricBoltIcon sx={{ fontSize: 20, color: "#38bdf8" }} />,
      title: t("buildOrSell.buildFeatures.techInnovation.title"),
      body: t("buildOrSell.buildFeatures.techInnovation.body"),
    },
  ];

  const sellFeatures: FeatureItemT[] = [
    {
      icon: <AddCircleOutlineIcon sx={{ fontSize: 20, color: "#38bdf8" }} />,
      title: t("buildOrSell.sellFeatures.expandServices.title"),
      body: t("buildOrSell.sellFeatures.expandServices.body"),
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 20, color: "#38bdf8" }} />,
      title: t("buildOrSell.sellFeatures.earnMore.title"),
      body: t("buildOrSell.sellFeatures.earnMore.body"),
    },
    {
      icon: <StarOutlineIcon sx={{ fontSize: 20, color: "#38bdf8" }} />,
      title: t("buildOrSell.sellFeatures.customerValue.title"),
      body: t("buildOrSell.sellFeatures.customerValue.body"),
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 20, color: "#38bdf8" }} />,
      title: t("buildOrSell.sellFeatures.comprehensiveSupport.title"),
      body: t("buildOrSell.sellFeatures.comprehensiveSupport.body"),
    },
  ];

  return (
    <Box sx={styles.buildOrSellSection}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography sx={styles.eyebrow}>{t("buildOrSell.eyebrow")}</Typography>
          <Typography component="h2" sx={styles.sectionHeading}>
            <Box component="span" sx={styles.gradientText}>
              {t("buildOrSell.headlineGradient")}
            </Box>{" "}
            {t("buildOrSell.headlineEnd")}
          </Typography>
        </Box>
        <Box
          ref={refCard1}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 6, md: 0 },
            opacity: inView1 ? 1 : 0,
            transform: inView1 ? "translateY(0)" : "translateY(40px)",
            transition: "opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s",
          }}
        >
          <Box ref={refCard2} sx={{ flex: 1, opacity: inView2 ? 1 : 0, transition: "opacity 0.6s ease 0.15s" }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography sx={styles.buildOrSellCardTitle}>
                <Box component="span" sx={styles.gradientText}>
                  {t("buildOrSell.buildTitle")}
                </Box>
                {t("buildOrSell.buildTitleSuffix")}
              </Typography>
            </Box>
            <Box sx={styles.featureGrid}>
              {buildFeatures.map((f, i) => (
                <FeatureRow key={f.title} feature={f} index={i} />
              ))}
            </Box>
          </Box>

          <Box sx={styles.verticalDivider} />
          <Box sx={styles.horizontalDivider} />

          <Box sx={{ flex: 1 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography sx={styles.buildOrSellCardTitle}>
                <Box component="span" sx={styles.gradientText}>
                  {t("buildOrSell.sellTitle")}
                </Box>
                {t("buildOrSell.sellTitleSuffix")}
              </Typography>
            </Box>
            <Box sx={styles.featureGrid}>
              {sellFeatures.map((f, i) => (
                <FeatureRow key={f.title} feature={f} index={i + 2} />
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          ref={refBottom}
          sx={{
            mt: { xs: 10, md: 14 },
            opacity: inViewBottom ? 1 : 0,
            transform: inViewBottom ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
          }}
        >
          <Typography variant="h3" sx={styles.whyChooseHeading}>
            {t("buildOrSell.whyChooseHeadingStart")}{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("buildOrSell.whyChooseHeadingGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.whyChooseBody}>{t("buildOrSell.whyChooseBody")}</Typography>

          <Grid container spacing={0} sx={{ mb: { xs: 8, md: 10 } }}>
            {[
              {
                value: t("buildOrSell.stats.activeMarkets.value"),
                label: t("buildOrSell.stats.activeMarkets.label"),
                description: t("buildOrSell.stats.activeMarkets.description"),
              },
              {
                value: t("buildOrSell.stats.overheadAdded.value"),
                label: t("buildOrSell.stats.overheadAdded.label"),
                description: t("buildOrSell.stats.overheadAdded.description"),
              },
              {
                value: t("buildOrSell.stats.revenueStreams.value"),
                label: t("buildOrSell.stats.revenueStreams.label"),
                description: t("buildOrSell.stats.revenueStreams.description"),
              },
            ].map((stat) => (
              <Grid key={stat.label} item xs={12} sm={4}>
                <Box sx={styles.statBorderBox}>
                  <Typography sx={styles.statValue}>{stat.value}</Typography>
                  <Typography sx={styles.statLabel}>{stat.label}</Typography>
                  <Typography sx={styles.statDescription}>{stat.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Button
            href={PARTNER_APPLICATION_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            sx={styles.getStartedButton}
          >
            {t("buildOrSell.getStartedCta")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

type WhyPartnerSectionPropsT = {
  t: TranslateFnT;
};

const WhyPartnerSection = ({ t }: WhyPartnerSectionPropsT) => {
  const { ref: headerRef, inView: headerInView } = useInView();
  const { ref: gridRef, inView: gridInView } = useInView(0.05);

  const whyItems = [
    {
      title: t("whyPartner.items.supplyChain.title"),
      body: t("whyPartner.items.supplyChain.body"),
      icon: <LocalShippingIcon />,
    },
    {
      title: t("whyPartner.items.leveragePartnerships.title"),
      body: t("whyPartner.items.leveragePartnerships.body"),
      icon: <HandshakeIcon />,
    },
    {
      title: t("whyPartner.items.reduceOverhead.title"),
      body: t("whyPartner.items.reduceOverhead.body"),
      icon: <TuneIcon />,
    },
    {
      title: t("whyPartner.items.qualityProjects.title"),
      body: t("whyPartner.items.qualityProjects.body"),
      icon: <VerifiedIcon />,
    },
    {
      title: t("whyPartner.items.guaranteedPayment.title"),
      body: t("whyPartner.items.guaranteedPayment.body"),
      icon: <PaidIcon />,
    },
  ];

  const cells: Array<{ type: "copy"; index: number } | { type: "photo"; index: number }> = [
    { type: "photo", index: 0 },
    { type: "copy", index: 0 },
    { type: "photo", index: 1 },
    { type: "photo", index: 2 },
    { type: "photo", index: 3 },
    { type: "copy", index: 1 },
    { type: "copy", index: 2 },
    { type: "copy", index: 3 },
    { type: "copy", index: 4 },
    { type: "photo", index: 4 },
  ];

  const photoSrcs: Record<number, string> = {
    0: "/img/assets/supply.jpg",
    1: "/img/assets/partner.jpg",
    2: "/img/assets/cost.jpg",
    3: "/img/assets/Projects.jpg",
    4: "/img/assets/payment.jpg",
  };

  return (
    <Box sx={styles.whyPartnerSection}>
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          ref={headerRef}
          sx={{
            textAlign: "center",
            mb: { xs: 8, md: 10 },
            opacity: headerInView ? 1 : 0,
            transform: headerInView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrow}>{t("whyPartner.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.whyPartnerHeading}>
            {t("whyPartner.headingStart")}{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("whyPartner.headingGradient")}
            </Box>
          </Typography>
        </Box>
      </Container>

      <Box ref={gridRef} sx={styles.whyPartnerGrid}>
        {cells.map((cell, i) => {
          const areaMap: Record<string, string> = {
            "photo-0": "photoA",
            "copy-0": "copy1",
            "photo-1": "photoB",
            "photo-2": "photoC",
            "photo-3": "photoD",
            "copy-1": "copy2",
            "copy-2": "copy3",
            "copy-3": "copy4",
            "copy-4": "copy5",
            "photo-4": "photoE",
          };
          const areaKey = `${cell.type}-${cell.index}`;
          const gridArea = areaMap[areaKey];

          if (cell.type === "copy") {
            const item = whyItems[cell.index];
            return (
              <Box
                key={areaKey}
                sx={{
                  gridArea: { md: gridArea },
                  background: "rgba(255,255,255,0.03)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(56,189,248,0.1)",
                  borderRadius: 0,
                  p: { xs: "48px 32px", md: "64px 56px" },
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  minHeight: { xs: "auto", md: "unset" },
                  opacity: gridInView ? 1 : 0,
                  transform: gridInView ? "translateY(0)" : "translateY(40px)",
                  transition: `opacity 0.7s ease ${i * 0.07}s, transform 0.7s ease ${
                    i * 0.07
                  }s, background 0.3s ease, border-color 0.3s ease`,
                  cursor: "default",
                  "&:hover": {
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(56,189,248,0.25)",
                  },
                  "&::before": {
                    content: "''",
                    position: "absolute",
                    inset: 0,
                    background: "radial-gradient(circle at 0% 0%, rgba(56,189,248,0.08) 0%, transparent 60%)",
                    pointerEvents: "none",
                  },
                }}
              >
                <Box sx={styles.whyPartnerCopyIconBox}>{item.icon}</Box>
                <Typography sx={styles.whyPartnerCopyTitle}>{item.title}</Typography>
                <Typography sx={styles.whyPartnerCopyBody}>{item.body}</Typography>
              </Box>
            );
          }

          return (
            <Box
              key={areaKey}
              sx={{
                gridArea: { md: gridArea },
                position: "relative",
                overflow: "hidden",
                minHeight: { xs: "300px", md: "unset" },
                opacity: gridInView ? 1 : 0,
                transform: gridInView ? "translateY(0)" : "translateY(40px)",
                transition: `opacity 0.7s ease ${i * 0.07}s, transform 0.7s ease ${i * 0.07}s`,
              }}
            >
              <Box component="img" src={photoSrcs[cell.index]} alt="" sx={styles.whyPartnerPhoto} />
              <Box sx={styles.whyPartnerPhotoOverlay} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

type BecomePartnerSectionPropsT = {
  t: TranslateFnT;
};

const BecomePartnerSection = ({ t }: BecomePartnerSectionPropsT) => {
  const { ref, inView } = useInView();

  const partnerRequirements = [
    {
      icon: <GppGoodIcon />,
      title: t("becomePartner.requirements.licensed.title"),
      body: t("becomePartner.requirements.licensed.body"),
    },
    {
      icon: <EmojiEventsIcon />,
      title: t("becomePartner.requirements.qualityFirst.title"),
      body: t("becomePartner.requirements.qualityFirst.body"),
    },
    {
      icon: <SchoolIcon />,
      title: t("becomePartner.requirements.alwaysLearning.title"),
      body: t("becomePartner.requirements.alwaysLearning.body"),
    },
    {
      icon: <HomeRepairServiceIcon />,
      title: t("becomePartner.requirements.workmanshipWarranty.title"),
      body: t("becomePartner.requirements.workmanshipWarranty.body"),
    },
  ];

  return (
    <Box sx={styles.becomePartnerSection}>
      <Box sx={styles.becomePartnerGlow} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          ref={ref}
          sx={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 8, md: 10 } }}>
            <Typography sx={styles.eyebrow}>{t("becomePartner.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.becomePartnerHeading}>
              {t("becomePartner.headingStart")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("becomePartner.headingGradient")}
              </Box>
            </Typography>
            <Typography sx={styles.becomePartnerBody}>{t("becomePartner.body")}</Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: { xs: 8, md: 10 } }}>
            {partnerRequirements.map((item, i) => (
              <Box key={item.title} sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(25% - 18px)" } }}>
                <Box
                  sx={{
                    background: "rgba(255,255,255,0.03)",
                    backdropFilter: "blur(16px)",
                    border: "1px solid rgba(56,189,248,0.12)",
                    borderRadius: "20px",
                    p: { xs: 4, md: 4 },
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    opacity: inView ? 1 : 0,
                    transform: inView ? "translateY(0)" : "translateY(28px)",
                    transition: `opacity 0.6s ease ${0.1 + i * 0.1}s, transform 0.6s ease ${0.1 + i * 0.1}s`,
                  }}
                >
                  <Box sx={styles.requirementCardGlow} />
                  <Box sx={styles.requirementIconBox}>{item.icon}</Box>
                  <Typography sx={styles.requirementTitle}>{item.title}</Typography>
                  <Typography sx={styles.requirementBody}>{item.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Button
              href={PARTNER_APPLICATION_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="large"
              sx={styles.getStartedButton}
            >
              {t("becomePartner.applyCta")}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

type PartnerTestimonialT = {
  quote: string;
  name: string;
  role: string;
  company: string;
  initials: string;
  avatarColor: string;
};

type PartnerTestimonialsSectionPropsT = {
  t: TranslateFnT;
};

const PartnerTestimonialsSection = ({ t }: PartnerTestimonialsSectionPropsT) => {
  const [active, setActive] = useState(0);
  const { ref, inView } = useInView();

  const partnerTestimonials: PartnerTestimonialT[] = [
    {
      quote: t("testimonials.quotes.caryS.quote"),
      name: t("testimonials.quotes.caryS.name"),
      role: t("testimonials.quotes.caryS.role"),
      company: t("testimonials.quotes.caryS.company"),
      initials: t("testimonials.quotes.caryS.initials"),
      avatarColor: "#1d4ed8",
    },
    {
      quote: t("testimonials.quotes.andresG.quote"),
      name: t("testimonials.quotes.andresG.name"),
      role: t("testimonials.quotes.andresG.role"),
      company: t("testimonials.quotes.andresG.company"),
      initials: t("testimonials.quotes.andresG.initials"),
      avatarColor: "#0e7490",
    },
    {
      quote: t("testimonials.quotes.jaredF.quote"),
      name: t("testimonials.quotes.jaredF.name"),
      role: t("testimonials.quotes.jaredF.role"),
      company: t("testimonials.quotes.jaredF.company"),
      initials: t("testimonials.quotes.jaredF.initials"),
      avatarColor: "#065f46",
    },
    {
      quote: t("testimonials.quotes.angelicaE.quote"),
      name: t("testimonials.quotes.angelicaE.name"),
      role: t("testimonials.quotes.angelicaE.role"),
      company: t("testimonials.quotes.angelicaE.company"),
      initials: t("testimonials.quotes.angelicaE.initials"),
      avatarColor: "#7c3aed",
    },
  ];

  const goTo = (index: number) => {
    if (index !== active) setActive(index);
  };
  const prev = () => goTo((active - 1 + partnerTestimonials.length) % partnerTestimonials.length);
  const next = () => goTo((active + 1) % partnerTestimonials.length);
  const dotKeys = partnerTestimonials.map((testimonial) => `dot-${testimonial.name}`);

  return (
    <Box sx={styles.testimonialsSection}>
      <Container maxWidth="lg">
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            mb: { xs: 6, md: 10 },
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrow}>{t("testimonials.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.testimonialsHeading}>
            {t("testimonials.headingStart")}{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("testimonials.headingGradient")}
            </Box>
          </Typography>
        </Box>
      </Container>

      <Box sx={{ position: "relative" }}>
        <IconButton onClick={prev} sx={{ ...styles.testimonialNavButton, left: { xs: 8, md: 16, lg: 24 } } as never}>
          <KeyboardArrowDownIcon sx={{ transform: "rotate(90deg)" }} />
        </IconButton>

        <Box sx={{ overflow: "hidden", px: { xs: 2, md: 8 } }}>
          <Box
            sx={
              {
                ...styles.testimonialCarouselTrack,
                transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
                transform: `translateX(calc(-${active} * (min(640px, 82vw) + 24px) + 50% - min(320px, 41vw)))`,
              } as never
            }
          >
            {partnerTestimonials.map((testimonial, index) => {
              const testimonialOpacity = (() => {
                if (index === active) return 1;
                return Math.abs(index - active) === 1 ? 0.4 : 0;
              })();
              return (
                <Box
                  key={testimonial.name}
                  sx={{
                    flex: "0 0 min(640px, 82vw)",
                    width: "min(640px, 82vw)",
                    opacity: testimonialOpacity,
                    transform: index === active ? "scale(1)" : "scale(0.72)",
                    transition: "opacity 0.4s cubic-bezier(0.22,1,0.36,1), transform 0.4s cubic-bezier(0.22,1,0.36,1)",
                    pointerEvents: index === active ? "auto" : "none",
                  }}
                >
                  <Box sx={styles.testimonialCardInner}>
                    <FormatQuoteIcon sx={styles.testimonialQuoteIcon} />
                    <Typography sx={styles.testimonialQuoteText}>&ldquo;{testimonial.quote}&rdquo;</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ ...styles.testimonialAvatarCircle, bgcolor: testimonial.avatarColor } as never}>
                        <Typography sx={{ color: "white", fontWeight: 700, fontSize: "0.82rem" }}>
                          {testimonial.initials}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={styles.testimonialName}>{testimonial.name}</Typography>
                        <Typography sx={styles.testimonialRole}>
                          {testimonial.role} · {testimonial.company}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <IconButton onClick={next} sx={{ ...styles.testimonialNavButton, right: { xs: 8, md: 16, lg: 24 } } as never}>
          <KeyboardArrowDownIcon sx={{ transform: "rotate(-90deg)" }} />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 5 }}>
        {dotKeys.map((key, dotIdx) => (
          <Box
            key={key}
            onClick={() => goTo(dotIdx)}
            sx={{
              width: dotIdx === active ? 24 : 8,
              height: 8,
              borderRadius: "4px",
              bgcolor: dotIdx === active ? "#38bdf8" : "rgba(255,255,255,0.2)",
              cursor: "pointer",
              transition: "all 0.35s ease",
              "&:hover": { bgcolor: dotIdx === active ? "#38bdf8" : "rgba(255,255,255,0.4)" },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

type WhyPowurCardT = {
  number: string;
  title: string;
  body: string;
};

type WhyPowurSectionPropsT = {
  t: TranslateFnT;
};

const WhyPowurSection = ({ t }: WhyPowurSectionPropsT) => {
  const { ref, inView } = useInView();

  const whyPowurCards: WhyPowurCardT[] = [
    {
      number: t("whyPowur.cards.experiencedLeadership.number"),
      title: t("whyPowur.cards.experiencedLeadership.title"),
      body: t("whyPowur.cards.experiencedLeadership.body"),
    },
    {
      number: t("whyPowur.cards.communityCulture.number"),
      title: t("whyPowur.cards.communityCulture.title"),
      body: t("whyPowur.cards.communityCulture.body"),
    },
    {
      number: t("whyPowur.cards.networkBackup.number"),
      title: t("whyPowur.cards.networkBackup.title"),
      body: t("whyPowur.cards.networkBackup.body"),
    },
    {
      number: t("whyPowur.cards.provenTrackRecord.number"),
      title: t("whyPowur.cards.provenTrackRecord.title"),
      body: t("whyPowur.cards.provenTrackRecord.body"),
    },
    {
      number: t("whyPowur.cards.technologyBuilt.number"),
      title: t("whyPowur.cards.technologyBuilt.title"),
      body: t("whyPowur.cards.technologyBuilt.body"),
    },
  ];

  return (
    <Box sx={styles.whyPowurSection}>
      <Container maxWidth="lg">
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
            mb: { xs: 6, md: 8 },
          }}
        >
          <Typography sx={styles.eyebrow}>{t("whyPowur.eyebrow")}</Typography>
          <Typography component="h2" sx={styles.whyPowurHeading}>
            {t("whyPowur.headingStart")}{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("whyPowur.headingGradient")}
            </Box>
          </Typography>
          <Box sx={styles.whyPowurQuoteWrapper}>
            <FormatQuoteIcon sx={{ fontSize: 32, color: "rgba(56,189,248,0.35)", flexShrink: 0, mt: 0.5 }} />
            <Typography sx={styles.whyPowurQuoteText}>
              {t("whyPowur.quote")}
              <Box component="span" sx={styles.whyPowurQuoteAttribution}>
                {t("whyPowur.quoteAttribution")}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Container>

      <Box sx={styles.whyPowurScrollContainer}>
        <Box sx={styles.whyPowurCardTrack}>
          {whyPowurCards.map((card, i) => (
            <Box
              key={card.number}
              sx={{
                flex: { xs: "0 0 320px", lg: 1 },
                borderRadius: "20px",
                overflow: "hidden",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(56,189,248,0.12)",
                backdropFilter: "blur(16px)",
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(40px)",
                transition: `opacity 0.65s ease ${0.08 + i * 0.1}s, transform 0.65s ease ${0.08 + i * 0.1}s`,
                "&:hover": {
                  border: "1px solid rgba(56,189,248,0.3)",
                  boxShadow: "0 0 40px rgba(56,189,248,0.08)",
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <Box sx={styles.whyPowurCardImageWrapper}>
                <Box
                  component="img"
                  src={`/img/assets/Partner-${i + 1}.jpg`}
                  alt={card.title}
                  sx={styles.whyPowurCardImage}
                />
                <Box sx={styles.whyPowurCardImageOverlay} />
              </Box>

              <Box sx={{ p: { xs: 3, md: 2.5 } }}>
                <Typography sx={styles.whyPowurCardTitle}>{card.title}</Typography>
                <Typography sx={styles.whyPowurCardBody}>{card.body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

type PlatformFeatureT = {
  icon: React.ReactNode;
  title: string;
  body: string;
  image: string;
};

type PowurPlatformSectionPropsT = {
  t: TranslateFnT;
};

const PowurPlatformSection = ({ t }: PowurPlatformSectionPropsT) => {
  const { ref, inView } = useInView();

  const platformFeatures: PlatformFeatureT[] = [
    {
      icon: <CampaignIcon />,
      title: t("platform.features.marketing.title"),
      body: t("platform.features.marketing.body"),
      image: "/img/assets/Asset-29@2xa.jpg",
    },
    {
      icon: <DescriptionIcon />,
      title: t("platform.features.proposal.title"),
      body: t("platform.features.proposal.body"),
      image: "/img/assets/2.jpg",
    },
    {
      icon: <BrandingWatermarkIcon />,
      title: t("platform.features.coBrand.title"),
      body: t("platform.features.coBrand.body"),
      image: "/img/assets/Cover-Slide-1.jpg",
    },
    {
      icon: <AccountBalanceIcon />,
      title: t("platform.features.financing.title"),
      body: t("platform.features.financing.body"),
      image: "/img/assets/partner.jpg",
    },
    {
      icon: <VisibilityIcon />,
      title: t("platform.features.transparentCogs.title"),
      body: t("platform.features.transparentCogs.body"),
      image: "/img/assets/COGs.png",
    },
    {
      icon: <MapIcon />,
      title: t("platform.features.activeMarkets.title"),
      body: t("platform.features.activeMarkets.body"),
      image: "/img/assets/Coverage-Map-5.png",
    },
  ];

  return (
    <Box sx={styles.powurPlatformSection}>
      <Container maxWidth="lg">
        <Box
          ref={ref}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 8, md: 10 },
            alignItems: { md: "flex-start" },
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Box sx={styles.powurPlatformSidebar}>
            <Typography sx={styles.eyebrow}>{t("platform.eyebrow")}</Typography>
            <Typography component="h2" sx={styles.powurPlatformHeading}>
              {t("platform.headingStart")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("platform.headingGradient")}
              </Box>
            </Typography>
            <Typography sx={styles.powurPlatformBody}>{t("platform.body")}</Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {platformFeatures.map((feature, i) => (
                <Box key={feature.title} sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.03)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(56,189,248,0.12)",
                      borderRadius: "20px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      opacity: inView ? 1 : 0,
                      transform: inView ? "translateY(0)" : "translateY(32px)",
                      transition: `opacity 0.6s ease ${0.05 + i * 0.08}s, transform 0.6s ease ${0.05 + i * 0.08}s`,
                    }}
                  >
                    <Box sx={styles.platformFeatureImageWrapper}>
                      <Box component="img" src={feature.image} alt={feature.title} sx={styles.platformFeatureImage} />
                    </Box>
                    <Box sx={{ p: { xs: 3.5, md: 4 }, flex: 1, position: "relative" }}>
                      <Box sx={styles.platformFeatureGlow} />
                      <Box sx={styles.platformFeatureIconBox}>{feature.icon}</Box>
                      <Typography sx={styles.platformFeatureTitle}>{feature.title}</Typography>
                      <Typography sx={styles.platformFeatureBody}>{feature.body}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

type PartnersCTASectionPropsT = {
  t: TranslateFnT;
};

const PartnersCTASection = ({ t }: PartnersCTASectionPropsT) => {
  const { ref, inView } = useInView();

  return (
    <Box sx={styles.ctaSection}>
      <Box component="img" src="/img/assets/JonathanBudd-partner.jpg" alt="" sx={styles.ctaBackgroundImage} />
      <Box sx={styles.ctaLeftGradient} />
      <Box sx={styles.ctaTopGradient} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, py: { xs: 10, md: 14 }, width: "100%" }}>
        <Box
          ref={ref}
          sx={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Box sx={{ maxWidth: { md: "640px" } }}>
            <Typography sx={styles.eyebrow}>{t("cta.eyebrow")}</Typography>
            <Typography component="h2" sx={styles.ctaHeading}>
              {t("cta.headingLine1")}
              <br />
              {t("cta.headingLine2Start")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("cta.headingGradient")}
              </Box>{" "}
              {t("cta.headingLine2End")}
              <br />
              {t("cta.headingLine3")}
            </Typography>
            <Box sx={styles.ctaBadgeRow}>
              {[
                "/img/assets/Inc5000_963.png",
                "/img/assets/Inc5000_61.png",
                "/img/assets/Inc5000_43.png",
                "/img/assets/Inc5000_130.png",
                "/img/assets/Credibility - bbb.png",
                "/img/assets/Credibility - Bcorp.png",
              ].map((src) => (
                <Box key={src} component="img" src={src} alt="" sx={styles.ctaBadgeImage} />
              ))}
            </Box>
            <Button
              href={PARTNER_APPLICATION_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              sx={styles.ctaButton}
            >
              {t("cta.joinCta")}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const PartnersContent = () => {
  const { t } = useTranslation("static_partners");

  return (
    <>
      <HeroSection t={t} />
      <BuildOrSellSection t={t} />
      <WhyPartnerSection t={t} />
      <BecomePartnerSection t={t} />
      <WhyPowurSection t={t} />
      <PartnerTestimonialsSection t={t} />
      <PowurPlatformSection t={t} />
      <PartnersCTASection t={t} />
    </>
  );
};

const PartnersPage = () => {
  const { t } = useTranslation("static_partners");

  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      keywords={t("meta.keywords")}
      ogTitle={t("meta.ogTitle")}
      ogDescription={t("meta.ogDescription")}
      twitterTitle={t("meta.twitterTitle")}
      twitterDescription={t("meta.twitterDescription")}
      noTopSpacer
    >
      <PartnersContent />
    </StaticPageLayout>
  );
};

export default PartnersPage;
